/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, MenuItem, IconButton, Alert, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TableContainer, Divider, Chip, Autocomplete, Tooltip } from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import { Snackbar } from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DocumentPicker from './DocumentPicker';
import DeleteIcon from '@mui/icons-material/Delete';


// 🛠️ NEW HELPER: Currency Adornment for the 'R' symbol
const CurrencyAdornment = () => (
    <Typography variant="body1" sx={{ mr: 1, color: 'text.secondary' }}>R</Typography>
);

// Locale-robust money parser - a bare parseFloat mangles values captured/imported in
// SA/European format ("1.964.495,30" -> 1.964, "1 964 495,30" -> 1). Handles those,
// "1,964,495.30", plain "1964495.30" and numbers.
const parseMoney = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    let s = String(val).trim().replace(/[^0-9.,-]/g, '');
    if (!s) return 0;
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    const decimalTail = (str, sep) => { const tail = str.slice(str.lastIndexOf(sep) + 1); return tail.length > 0 && tail.length <= 2; };
    if (lastComma > -1 && lastDot > -1) {
        s = lastComma > lastDot
            ? s.slice(0, lastComma).replace(/[.,]/g, '') + '.' + s.slice(lastComma + 1)
            : s.replace(/,/g, '');
    } else if (lastComma > -1) {
        s = decimalTail(s, ',')
            ? s.slice(0, lastComma).replace(/,/g, '') + '.' + s.slice(lastComma + 1)
            : s.replace(/,/g, '');
    } else if ((s.match(/\./g) || []).length > 1) {
        s = s.replace(/\./g, '');
    }
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
};

const formatMoney = (val) => `R ${parseMoney(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Define all possible asset categories
const ASSET_CATEGORIES = {
    'Immovable Property': 'immovable_property',
    'Movable Asset': 'movable_asset',
    'Bank Account': 'bank_account',
    'Vehicle': 'vehicle',
    'Policy': 'policy',
    'Other Asset': 'other_asset',
};

// Vehicle and Policy are only offered as asset categories on NPA matters.
const NPA_ONLY_CATEGORIES = ['Vehicle', 'Policy'];

// 🛠️ ADDED: Consistent Date Formatting Helper
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toISOString().split('T')[0];
};

// The Erf field is free text and users often type "Erf 486" into it, so blindly prefixing
// "Erf " printed "Erf Erf 486".
const formatErf = (erfNo) => {
    const erf = String(erfNo || '').trim();
    if (!erf) return '';
    return /^erf\b/i.test(erf) ? erf : `Erf ${erf}`;
};

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Printed Assets List: the "Details" column gives the physical address for property and the
// make / model / year / registration for vehicles; any other category just shows its name.
const getPrintDetails = (asset) => {
    // Property: everything typed under 1.1 Property Information that isn't the description /
    // Erf / valuation (which have their own columns) - address, title deed and deeds office.
    if (asset.assetCategory === 'Immovable Property') {
        const lines = [
            asset.streetAddress || 'N/A',
            asset.titleDeedNo ? `Title Deed No: ${asset.titleDeedNo}` : '',
            asset.deedsOffice ? `Deeds Office: ${asset.deedsOffice}` : '',
        ].filter(Boolean);
        return lines;
    }
    if (asset.assetCategory === 'Vehicle') {
        const vehicle = [asset.vehicleMake, asset.vehicleModel, asset.vehicleYear].filter(Boolean).join(' ');
        const reg = asset.registrationNumber ? `Reg No: ${asset.registrationNumber}` : '';
        return [vehicle, reg].filter(Boolean).join(' — ') || 'N/A';
    }
    return asset.assetCategory || 'N/A';
};

// Printed Assets List "Description": for property the description plus Erf number (the
// address has its own column), otherwise the same description used on screen.
const getPrintDescription = (asset, isNPA) => {
    if (asset.assetCategory === 'Immovable Property') {
        return [asset.asset_description, formatErf(asset.erfNo)].filter(Boolean).join(' — ') || 'N/A';
    }
    if (asset.assetCategory === 'Vehicle') return asset.asset_description || 'Vehicle';
    return getAssetDescription(asset, isNPA);
};

// Builds a meaningful Description fallback per asset category, since several
// categories (Bank Account, Vehicle, Policy) don't populate asset_description.
const getAssetDescription = (asset, isNPA = false) => {
    // NPA matters: the Property Description the user types (and can edit later) was never
    // shown in the table - only Erf + address were - so editing it looked like it did nothing.
    // Lead with that description, keeping Erf/address as context, without doubling the word
    // "Erf" when the Erf field already starts with it.
    if (isNPA && asset.assetCategory === 'Immovable Property' && asset.asset_description) {
        const erf = asset.erfNo ? (/^erf\b/i.test(String(asset.erfNo).trim()) ? asset.erfNo : `Erf ${asset.erfNo}`) : '';
        const context = [erf, asset.streetAddress].filter(Boolean).join(', ');
        return context ? `${asset.asset_description} (${context})` : asset.asset_description;
    }
    // Immovable Property: always show the Erf and its physical address together.
    if (asset.assetCategory === 'Immovable Property') {
        const parts = [];
        if (asset.erfNo) parts.push(formatErf(asset.erfNo));
        if (asset.streetAddress) parts.push(asset.streetAddress);
        if (parts.length === 0 && asset.asset_description) return asset.asset_description;
        return parts.join(' — ') || 'N/A';
    }
    if (asset.asset_description) return asset.asset_description;
    if (asset.streetAddress) return asset.streetAddress;
    if (asset.assetCategory === 'Bank Account') {
        const bank = [asset.cashBankName, asset.cashAccountHolder].filter(Boolean).join(' - ');
        if (bank) return bank;
    }
    if (asset.assetCategory === 'Vehicle') {
        const vehicle = [asset.vehicleMake, asset.vehicleModel, asset.vehicleYear].filter(Boolean).join(' ');
        if (vehicle) return asset.registrationNumber ? `${vehicle} (${asset.registrationNumber})` : vehicle;
    }
    if (asset.assetCategory === 'Policy') {
        const policy = [asset.insurerName, asset.policyNo].filter(Boolean).join(' — ');
        if (policy) return policy;
    }
    if (asset.defName) return asset.defName;
    return 'N/A';
};

const ImmoveableAssetsTab = ({ matter }) => {
    const [assets, setAssets] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);

    // State definitions
    const [asset_description, setAssetDescription] = useState("");
    const [occupied, setOccupied] = useState("");
    const [monthlyRentalAmount, setMonthlyRentalAmount] = useState("");
    const [section80ApprovalDate, setSection80ApprovalDate] = useState("");
    const [auctioneer, setAuctioneer] = useState("");
    const [auctionDate, setAuctionDate] = useState("");
    const [purchaser, setPurchaser] = useState("");
    const [purchased_date, setPurchasedDate] = useState("");
    const [sold_by, setSoldBy] = useState("");
    const [purchasePrice, setPurchasePrice] = useState("");
    const [valuationAmount, setValuationAmount] = useState("");
    const [estForcedSaleValue, setEstForcedSaleValue] = useState(""); // 🎯 NEW: feeds "Est Forced Sale Value" on the Curator's Report Section 9 schedule
    const [emailAddress, setEmailAddress] = useState("");
    const [attorney, setAttorney] = useState("");
    const [address, setAddress] = useState("");
    const [phoneNo, setPhoneNo] = useState("");
    const [emailAddress2, setEmailAddress2] = useState("");
    const [emailAddress3, setEmailAddress3] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [transactionInstructionDate, setTransactionInstructionDate] = useState("");
    const [transferDate, setTransferDate] = useState("");
    const [costsReceivedDate, setCostsReceivedDate] = useState("");
    const [section89Costs, setSection89Costs] = useState("");
    const [provisionalDividendPaid, setProvisionalDividendPaid] = useState("");
    const [finalPaymentDate, setFinalPaymentDate] = useState("");
    const [mortgageBondRegistered, setMortgageBondRegistered] = useState(false); // 🎯 NEW: optional - not every property has a bond
    const [bondBankName, setBondBankName] = useState("");
    const [bondAccountNo, setBondAccountNo] = useState("");
    const [bondNo, setBondNo] = useState("");
    const [bondAddress, setBondAddress] = useState("");
    const [bondAdditionalAddress, setBondAdditionalAddress] = useState("");
    const [bondFaxNumber, setBondFaxNumber] = useState("");
    const [bondPhoneNo, setBondPhoneNo] = useState("");
    const [bondEmailAddress, setBondEmailAddress] = useState("");
    const [bondClaimProved, setBondClaimProved] = useState("");
    const [bondClaimAmount, setBondClaimAmount] = useState("");
    const [bondReference, setBondReference] = useState("");
    const [comments, setComments] = useState("");
    const [claimApproved, setClaimApproved] = useState(false);
    const [claimRejected, setClaimRejected] = useState(false);
    const [contactPerson, setContactPerson] = useState('');
    const [contactPersonEmail, setContactPersonEmail] = useState('');
    const [contactPerson2, setContactPerson2] = useState('');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [tenantName, setTenantName] = useState('');
    const [tenantContact, setTenantContact] = useState('');
    const [tenantContactPerson, setTenantContactPerson] = useState('');
    const [auctioneerContactPerson, setAuctioneerContactPerson] = useState('');
    const [auctioneerPhone, setAuctioneerPhone] = useState('');
    const [auctioneerEmail, setAuctioneerEmail] = useState(''); // 🎯 4.4: was missing entirely for Property assets
    const [tenderNo, setTenderNo] = useState('');
    const [vinNo, setVinNo] = useState('');
    const [otherSubCategory, setOtherSubCategory] = useState('');
    const [defName, setDefName] = useState('');
    const [defIdNo, setDefIdNo] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountNo, setAccountNo] = useState('');
    const [accountType, setAccountType] = useState('');
    const [holdOnAccount, setHoldOnAccount] = useState(false);
    const [policyNo, setPolicyNo] = useState('');
    const [inceptionDate, setInceptionDate] = useState('');
    const [nameOfInsured, setNameOfInsured] = useState('');

    // NPA: Def/Resp linkage for Bank Account assets
    const [linkedRespondentId, setLinkedRespondentId] = useState('');
    const [linkedRespondentName, setLinkedRespondentName] = useState('');

    // NPA: Vehicle category
    const [vehicleMake, setVehicleMake] = useState('');
    const [vehicleModel, setVehicleModel] = useState('');
    const [vehicleYear, setVehicleYear] = useState('');

    // NPA: Policy category
    const [insurerName, setInsurerName] = useState('');
    const [policyType, setPolicyType] = useState('');

    const [isMovableAssetEncumbered, setIsMovableAssetEncumbered] = useState(false);
    const [movableEncumbranceType, setMovableEncumbranceType] = useState('');
    const [otherMovableEncumbranceType, setOtherMovableEncumbranceType] = useState('');

    const [movableAuctioneerName, setMovableAuctioneerName] = useState('');
    const [movableAuctioneerContactPerson, setMovableAuctioneerContactPerson] = useState('');
    const [movableAuctioneerPhone, setMovableAuctioneerPhone] = useState('');
    const [movableAuctioneerEmail, setMovableAuctioneerEmail] = useState('');

    const [cashSource, setCashSource] = useState('');
    const [otherCashSource, setOtherCashSource] = useState('');

    const [cashReceivedFrom, setCashReceivedFrom] = useState('');
    const [cashContactPerson, setCashContactPerson] = useState('');
    const [cashPhoneNo, setCashPhoneNo] = useState('');
    const [cashEmailAddress, setCashEmailAddress] = useState('');
    const [cashBankName, setCashBankName] = useState('');
    const [cashAccountHolder, setCashAccountHolder] = useState('');
    const [cashAccountNumber, setCashAccountNumber] = useState('');
    const [cashAccountType, setCashAccountType] = useState('');
    const [supportingDocuments, setSupportingDocuments] = useState([]);

    const [depositPaid, setDepositPaid] = useState(false);
    const [depositAmount, setDepositAmount] = useState('');
    const [proceedsReceivedDate, setProceedsReceivedDate] = useState('');
    const [amountPaidToEstate, setAmountPaidToEstate] = useState('');
    const [assetFinalised, setAssetFinalised] = useState(false);
    // 🎯 NEW: State for linked claim
    const [linkedClaimId, setLinkedClaimId] = useState('');
    const [linkedClaimName, setLinkedClaimName] = useState('');
    const [linkedClaimAmount, setLinkedClaimAmount] = useState('');
    const [offerToPurchaseUrl, setOfferToPurchaseUrl] = useState(null);

    // --- NEW FIELDS (HOA, Municipal, Caveat, Linkages) ---
    const [hoaInformation, setHoaInformation] = useState('');
    const [hoaStatement, setHoaStatement] = useState('');
    const [municipalInformation, setMunicipalInformation] = useState('');
    const [municipalStatement, setMunicipalStatement] = useState('');
    const [caveatRegistered, setCaveatRegistered] = useState(false);
    const [linkedCreditor, setLinkedCreditor] = useState('');
    const [linkedCashbookTransaction, setLinkedCashbookTransaction] = useState('');
    const [partOfEstate, setPartOfEstate] = useState(true);
    const [erfNo, setErfNo] = useState('');
    const [streetAddress, setStreetAddress] = useState('');
    const [titleDeedNo, setTitleDeedNo] = useState('');
    const [deedsOffice, setDeedsOffice] = useState('');
    const [caveatDate, setCaveatDate] = useState('');
    const [caveatDocumentUrl, setCaveatDocumentUrl] = useState(null); // 🎯 4.4: link/upload caveat document
    const [securityAtProperty, setSecurityAtProperty] = useState(false); // 🎯 4.4
    const [noticeToVacateDate, setNoticeToVacateDate] = useState(''); // 🎯 4.4
    const [rentCurrentlyCollected, setRentCurrentlyCollected] = useState(false); // 🎯 4.4
    const [rentCollectedBy, setRentCollectedBy] = useState(''); // 🎯 4.4
    const [rentalOtherInfo, setRentalOtherInfo] = useState(''); // 🎯 4.4

    const [user, setUser] = useState(null);
    const [estate, setEstate] = useState(null);
    const [assetCategory, setAssetCategory] = useState('');
    const [payments, setPayments] = useState('');

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [docPickerOpen, setDocPickerOpen] = useState(false);
    const [docPickerTarget, setDocPickerTarget] = useState('offer'); // 🎯 4.4: 'offer' | 'caveat' - which field the picker writes into
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [selectedTab, setSelectedTab] = useState(0);
    const [allClaims, setAllClaims] = useState([]); // To store all claims for linking
    const [assetTypeSelectionOpen, setAssetTypeSelectionOpen] = useState(false);
    const [selectedAssetTypeForNew, setSelectedAssetTypeForNew] = useState('');

    const legalMatterId = matter?.id || "";
    const isNPAMatter = matter?.estateClassification === 'NPA' || matter?.estateClassification === 'Npa Matters' || matter?.estateClassification === 'Curator Appointments';

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    // 3. Printing of Assets Table: same client-side "open a formatted window and print()"
    // approach as the Creditors List (see ClaimsTab.js handlePrint) - a real printable
    // document, not the Excel export this replaces (which wasn't working correctly).
    const handlePrintAssets = () => {
        if (!assets || assets.length === 0) {
            setSnackbarMessage("No assets found to print.");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
            return;
        }

        // [header, align] - align 'r' right-aligns money columns.
        const columns = isNPAMatter
            ? [['No.', 'c'], ['Description', 'l'], ['Address / Details', 'l'], ['Valuation Amount', 'r'], ['Defendant / Respondent', 'l']]
            : [['No.', 'c'], ['Description', 'l'], ['Address / Details', 'l'], ['Valuation Amount', 'r'], ['Sold Amount', 'r'], ['Linked Creditor', 'l'], ['Linked Claim No.', 'l'], ['Status', 'c']];

        const totalValuation = assets.reduce((sum, a) => sum + parseMoney(a.valuationAmount || 0), 0);

        const rowsHtml = assets.map((asset, idx) => {
            const linkedClaimNumber = asset.linkedClaimId
                ? (allClaims.find(c => String(c.id) === String(asset.linkedClaimId))?.claimNumber || asset.linkedClaimId)
                : 'N/A';
            const valuation = asset.valuationAmount ? formatMoney(asset.valuationAmount) : 'N/A';
            const cells = isNPAMatter
                ? [
                    idx + 1,
                    getPrintDescription(asset, true),
                    getPrintDetails(asset),
                    valuation,
                    asset.linkedRespondentName || asset.defName || 'N/A',
                ]
                : [
                    idx + 1,
                    getPrintDescription(asset, false),
                    getPrintDetails(asset),
                    valuation,
                    asset.amountPaidToEstate ? formatMoney(asset.amountPaidToEstate) : 'N/A',
                    asset.linkedClaimName || 'N/A',
                    linkedClaimNumber,
                    asset.assetFinalised ? 'Dealt With' : 'Outstanding',
                ];
            return '<tr>' + cells.map((c, i) => `<td class="${columns[i][1]}">${Array.isArray(c) ? c.map(escapeHtml).join('<br>') : escapeHtml(c)}</td>`).join('') + '</tr>';
        }).join('');

        const printedOn = new Date().toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' });
        const matterTitle = escapeHtml(matter?.matterName || matter?.estateNo || 'N/A');
        const matterRef = matter?.estateNo ? `<div><strong>Reference:</strong> ${escapeHtml(matter.estateNo)}</div>` : '';

        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Assets List - ${matterTitle}</title>
<style>
  @page { size: A4 landscape; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #111; margin: 0; }
  .header { border-bottom: 2px solid #1a3c6e; padding-bottom: 8px; margin-bottom: 10px; }
  .header h1 { font-size: 16pt; margin: 0 0 2px; color: #1a3c6e; }
  .header .sub { font-size: 10pt; color: #444; }
  .meta { display: flex; justify-content: space-between; font-size: 9.5pt; margin: 0 0 10px; color: #333; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #b8b8b8; padding: 5px 7px; vertical-align: top; word-wrap: break-word; }
  th { background: #1a3c6e; color: #fff; font-size: 9pt; text-transform: uppercase; letter-spacing: .3px; }
  tbody tr:nth-child(even) td { background: #f4f6fa; }
  .l { text-align: left; } .r { text-align: right; white-space: nowrap; } .c { text-align: center; }
  th:first-child, td:first-child { width: 34px; }
  tfoot td { font-weight: bold; background: #e8edf5; }
  .footer { margin-top: 12px; font-size: 8.5pt; color: #666; }
</style></head><body>
  <div class="header"><h1>Assets List</h1><div class="sub">${matterTitle}</div></div>
  <div class="meta"><div>${matterRef}<div><strong>Total Assets:</strong> ${assets.length}</div></div><div><div><strong>Printed:</strong> ${printedOn}</div></div></div>
  <table>
    <thead><tr>${columns.map(([h, a]) => `<th class="${a}">${h}</th>`).join('')}</tr></thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot><tr><td colspan="3" class="r">Total Valuation</td><td class="r">${escapeHtml(formatMoney(totalValuation))}</td><td colspan="${columns.length - 4}"></td></tr></tfoot>
  </table>
  <div class="footer">Generated from Insollex on ${printedOn}.</div>
</body></html>`;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const handleAssetCategoryChange = (e) => {
        const newCategory = e.target.value;
        setAssetCategory(newCategory);
    };

    const fetchUser = async () => {
        try {
            const res = await axios.get("/api/user", { withCredentials: true });
            if (typeof res.data !== "object" || res.data === null) {
                setUser(null);
                return;
            }
            if (!res.data.role) {
                res.data.role = "guest";
            }
            setUser(res.data);
        } catch {
            setUser(null);
        }
    };

    const sendAttorneyEmail = async () => {
      // Use emailAddress2 for the recipient
      if (!emailAddress2) {
          setSnackbarMessage("Attorney's email address is missing.");
          setSnackbarSeverity("warning");
          setOpenSnackbar(true);
          return;
      }
  
      try {
          await axios.post("/api/immoveable-assets/email", {
              // Target the Attorney's email address
              to: emailAddress2, 
              // Include asset details for context (using asset_description from state)
              asset_description: asset_description || "No description provided",
          });
  
          setSnackbarMessage("Email sent to attorney successfully!");
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
  
      } catch (err) {
          console.error("Error sending attorney email:", err);
          setSnackbarMessage("Failed to send email to attorney.");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
      }
  };
    
    const resetForm = () => {
      // Reset ALL Text Fields and associated states to empty string
      setAssetDescription("");
      setOccupied("");
      setMonthlyRentalAmount("");
      setSection80ApprovalDate("");
      setAuctioneer("");
      setAuctionDate("");
      setPurchaser("");
      setPurchasedDate("");
      setSoldBy("");
      setPurchasePrice("");
      setValuationAmount("");
      setEstForcedSaleValue("");
      setEmailAddress("");
      setAttorney("");
      setAddress("");
      setPhoneNo("");
      setEmailAddress2("");
      setEmailAddress3("");
      setMobileNumber("");
      setTransactionInstructionDate("");
      setTransferDate("");
      setCostsReceivedDate("");
      setSection89Costs("");
      setProvisionalDividendPaid("");
      setFinalPaymentDate("");
      setBondBankName("");
      setBondAccountNo("");
      setBondNo("");
      setBondAddress("");
      setBondAdditionalAddress("");
      setBondFaxNumber("");
      setBondPhoneNo("");
      setBondEmailAddress("");
      setBondClaimProved("");
      setBondClaimAmount("");
      setBondReference("");
      setComments("");
      
      // Reset Categorization and indexing states
      setAssetCategory(''); // Select field
      setPayments('');      // Decimal/number field
      setContactPerson('');
      setContactPersonEmail('');
      setContactPerson2('');
      setRegistrationNumber('');
      setTenantName('');
      setTenantContact('');
      setTenantContactPerson('');
      setAuctioneerContactPerson('');
      setAuctioneerPhone('');
      setAuctioneerEmail('');
      setTenderNo('');
      setVinNo('');
      setOtherSubCategory('');
      setDefName('');
      setDefIdNo('');
      setBankName('');
      setAccountNo('');
      setAccountType('');
      setHoldOnAccount(false);
      setPolicyNo('');
      setInceptionDate('');
      setNameOfInsured('');

      setLinkedRespondentId('');
      setLinkedRespondentName('');
      setVehicleMake('');
      setVehicleModel('');
      setVehicleYear('');
      setInsurerName('');
      setPolicyType('');

      setIsMovableAssetEncumbered(false);
      setMovableEncumbranceType('');
      setOtherMovableEncumbranceType('');

      setMovableAuctioneerName('');
      setMovableAuctioneerContactPerson('');
      setMovableAuctioneerPhone('');
      setMovableAuctioneerEmail('');

      setCashSource('');
      setOtherCashSource('');

      setCashReceivedFrom('');
      setCashContactPerson('');
      setCashPhoneNo('');
      setCashEmailAddress('');
      setCashBankName('');
      setCashAccountHolder('');
      setCashAccountNumber('');
      setCashAccountType('');

      setSupportingDocuments([]);
      
      setDepositPaid(false);
      setDepositAmount('');
      setProceedsReceivedDate('');
      setAmountPaidToEstate('');
      setAssetFinalised(false);
      // Reset linked claim fields
      setLinkedClaimId('');
      setLinkedClaimName('');
      setLinkedClaimAmount('');
      setOfferToPurchaseUrl(null);

      setHoaInformation('');
      setHoaStatement('');
      setMunicipalInformation('');
      setMunicipalStatement('');
      setPartOfEstate(true);
      setMortgageBondRegistered(false);
      setCaveatRegistered(false);
      setCaveatDocumentUrl(null);
      setSecurityAtProperty(false);
      setNoticeToVacateDate('');
      setRentCurrentlyCollected(false);
      setRentCollectedBy('');
      setRentalOtherInfo('');
      setLinkedCreditor('');
      setLinkedCashbookTransaction('');
      setErfNo('');
      setStreetAddress('');
      setTitleDeedNo('');
      setDeedsOffice('');
      setCaveatDate('');
      
      // Reset index (important for returning to 'Add New' state)
      setEditingAsset(null);
  };

    const sanitizeAsset = (asset) => ({
        ...asset,
        bondClaimAmount: asset.bondClaimAmount ?? "",
    });

    const populateFormFields = (selected) => {
      setAttorney(selected.attorney || "");
      setPurchaser(selected.purchaser || "");
      setPurchasedDate(formatDateForInput(selected.purchased_date || selected.purchasedDate));
      setSoldBy(selected.sold_by || selected.soldBy || "");
      setAssetDescription(selected.asset_description || selected.assetDescription || "");
      setOccupied(selected.occupied || "");
      setMonthlyRentalAmount(selected.monthly_rental_amount || selected.monthlyRentalAmount || "");
      setSection80ApprovalDate(formatDateForInput(selected.section_80_approval_date || selected.section80ApprovalDate));
      setAuctioneer(selected.auctioneer || "");
      setAuctionDate(formatDateForInput(selected.auction_date || selected.auctionDate));
      setPurchasePrice(selected.purchase_price || selected.purchasePrice || "");
      setValuationAmount(selected.valuationAmount || "");
      setEstForcedSaleValue(selected.estForcedSaleValue || "");
      setEmailAddress(selected.email_address || selected.emailAddress || "");
      setAddress(selected.address || "");
      setPhoneNo(selected.phone_no || selected.phoneNo || "");
      setEmailAddress2(selected.email_address2 || selected.emailAddress2 || "");
      setEmailAddress3(selected.email_address3 || selected.emailAddress3 || "");
      setMobileNumber(selected.mobile_number || selected.mobileNumber || "");
      setTransactionInstructionDate(formatDateForInput(selected.transaction_instruction_date || selected.transactionInstructionDate));
      setTransferDate(formatDateForInput(selected.transfer_date || selected.transferDate));
      setCostsReceivedDate(formatDateForInput(selected.costs_received_date || selected.costsReceivedDate));
      setSection89Costs(selected.section_89_costs || selected.section89Costs || "");
      setProvisionalDividendPaid(selected.provisional_dividend_paid || selected.provisionalDividendPaid || "");
      setFinalPaymentDate(formatDateForInput(selected.final_payment_date || selected.finalPaymentDate));
      setMortgageBondRegistered(!!selected.mortgageBondRegistered);
      setBondBankName(selected.bondBankName || "");
      setBondAccountNo(selected.bondAccountNo || "");
      setBondNo(selected.bondNo || "");
      setBondAddress(selected.bondAddress || "");
      setBondAdditionalAddress(selected.bondAdditionalAddress || "");
      setBondFaxNumber(selected.bondFaxNumber || "");
      setBondPhoneNo(selected.bondPhoneNo || "");
      setBondEmailAddress(selected.bondEmailAddress || "");
      setBondClaimProved(selected.bondClaimProved || "");
      setBondClaimAmount(selected.bondClaimAmount || "");
      setBondReference(selected.bondReference || "");
      setComments(selected.comments || "");
      setAssetCategory(selected.assetCategory || "");
      setPayments(selected.payments || "");
      setContactPerson(selected.contactPerson || "");
      setContactPersonEmail(selected.contactPersonEmail || "");
      setContactPerson2(selected.contactPerson2 || "");
      setRegistrationNumber(selected.registrationNumber || "");
      setTenantName(selected.tenantName || "");
      setTenantContact(selected.tenantContact || "");
      setTenantContactPerson(selected.tenantContactPerson || "");
      setAuctioneerContactPerson(selected.auctioneerContactPerson || "");
      setAuctioneerPhone(selected.auctioneerPhone || "");
      setAuctioneerEmail(selected.auctioneerEmail || "");
      setTenderNo(selected.tenderNo || "");
      setVinNo(selected.vinNo || "");
      setOtherSubCategory(selected.otherSubCategory || "");
      setDefName(selected.defName || "");
      setDefIdNo(selected.defIdNo || "");
      setBankName(selected.bankName || "");
      setAccountNo(selected.accountNo || "");
      setAccountType(selected.accountType || "");
      setHoldOnAccount(!!selected.holdOnAccount);
      setPolicyNo(selected.policyNo || "");
      setInceptionDate(formatDateForInput(selected.inceptionDate));
      setNameOfInsured(selected.nameOfInsured || "");

      setLinkedRespondentId(selected.linkedRespondentId || "");
      setLinkedRespondentName(selected.linkedRespondentName || "");
      setVehicleMake(selected.vehicleMake || "");
      setVehicleModel(selected.vehicleModel || "");
      setVehicleYear(selected.vehicleYear || "");
      setInsurerName(selected.insurerName || "");
      setPolicyType(selected.policyType || "");

      setIsMovableAssetEncumbered(!!selected.isMovableAssetEncumbered);
      setMovableEncumbranceType(selected.movableEncumbranceType || '');
      setOtherMovableEncumbranceType(selected.otherMovableEncumbranceType || '');

      setMovableAuctioneerName(selected.movableAuctioneerName || '');
      setMovableAuctioneerContactPerson(selected.movableAuctioneerContactPerson || '');
      setMovableAuctioneerPhone(selected.movableAuctioneerPhone || '');
      setMovableAuctioneerEmail(selected.movableAuctioneerEmail || '');

      setCashSource(selected.cashSource || '');
      setOtherCashSource(selected.otherCashSource || '');

      setCashReceivedFrom(selected.cashReceivedFrom || '');
      setCashContactPerson(selected.cashContactPerson || '');
      setCashPhoneNo(selected.cashPhoneNo || '');
      setCashEmailAddress(selected.cashEmailAddress || '');
      setCashBankName(selected.cashBankName || '');
      setCashAccountHolder(selected.cashAccountHolder || '');
      setCashAccountNumber(selected.cashAccountNumber || '');
      setCashAccountType(selected.cashAccountType || '');

      setSupportingDocuments(selected.supportingDocuments || []);

      setDepositPaid(!!selected.depositPaid);
      setDepositAmount(selected.depositAmount || "");
      setProceedsReceivedDate(formatDateForInput(selected.proceedsReceivedDate));
      setAmountPaidToEstate(selected.amountPaidToEstate || "");
      setAssetFinalised(!!selected.assetFinalised);
      setLinkedClaimId(selected.linkedClaimId || '');
      setLinkedClaimName(selected.linkedClaimName || '');
      setLinkedClaimAmount(selected.linkedClaimAmount || '');
      setOfferToPurchaseUrl(selected.offerToPurchaseUrl || null);

      setHoaInformation(selected.hoaInformation || "");
      setHoaStatement(selected.hoaStatement || "");
      setMunicipalInformation(selected.municipalInformation || "");
      setMunicipalStatement(selected.municipalStatement || "");
      setPartOfEstate(selected.partOfEstate !== false); // Default to true if undefined
      setCaveatRegistered(!!selected.caveatRegistered);
      setLinkedCreditor(selected.linkedCreditor || "");
      setLinkedCashbookTransaction(selected.linkedCashbookTransaction || "");
      setErfNo(selected.erfNo || "");
      setStreetAddress(selected.streetAddress || "");
      setTitleDeedNo(selected.titleDeedNo || "");
      setDeedsOffice(selected.deedsOffice || "");
      setCaveatDate(formatDateForInput(selected.caveatDate));
      setCaveatDocumentUrl(selected.caveatDocumentUrl || null);
      setSecurityAtProperty(!!selected.securityAtProperty);
      setNoticeToVacateDate(formatDateForInput(selected.noticeToVacateDate));
      setRentCurrentlyCollected(!!selected.rentCurrentlyCollected);
      setRentCollectedBy(selected.rentCollectedBy || "");
      setRentalOtherInfo(selected.rentalOtherInfo || "");
  };

  const fetchAllClaims = async () => {
    if (!legalMatterId) return;
    try {
      const response = await axios.get(`/api/legal-matter/${legalMatterId}`);
      // Assuming claimsList is part of the matter object
      setAllClaims(response.data.claimsList || []);
    } catch (error) {
      console.error("Error fetching all claims:", error);
      setSnackbarMessage("Error fetching all claims.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

    // --- Fetch Assets Logic ---
const fetchAssets = async () => {
  try {
      // 🧹 CLEAR FORM FIRST: Prevents data from one tab bleeding into another while loading or if it's empty
      resetForm();

      let data = [];
      
      // 2. Determine Endpoint and Fetch Data
      
      // B. Handle New Database Immovable/Encumbered/Free Residue Assets (Requires legalMatterId)
      // This is the combined logic for all New DB Immovable asset types.
      if (legalMatterId) {
          if (!legalMatterId) { 
              setSnackbarMessage('A Legal Matter ID is required to fetch assets.'); 
              setSnackbarSeverity('warning');
              setOpenSnackbar(true);
              return; 
          }
          const endpoint = '/api/immoveables';
          const response = await axios.get(endpoint, { params: { legalMatterId } }); // Fetch all assets, no status filter
          data = Array.isArray(response.data) ? response.data : [];

      // C. Fallback: If no valid ID, no valid key, or invalid key for the given ID type.
      } else {
          if (!legalMatterId) {
              setSnackbarMessage('Please provide a valid Legal Matter ID.');
          } else {
              setSnackbarMessage(`No assets found for this matter.`);
          }
          setAssets([]);
          setSnackbarSeverity('warning');
          setOpenSnackbar(true);
          return;
      }

      // 3. Process and Populate Form
      if (Array.isArray(data)) {
          setAssets(data);
      } else {
          setAssets([]); // Ensure assets is an array even if data is not
          setSnackbarMessage('No assets found for the selected tab.');
          setSnackbarSeverity('warning');
          setOpenSnackbar(true);
      }
  } catch (error) {
      console.error('Error fetching assets:', error);
      setSnackbarMessage('Error fetching assets.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
  }
};
  
  // --- Add/Save Logic ---
  const handleAddOrSaveTransaction = async () => {
      try {
          // 🎯 NEW: Ensure linked claim details are captured
          if (!assetCategory || !legalMatterId) {
              setSnackbarMessage("Please select an Asset Category.");
              setSnackbarSeverity("warning"); setOpenSnackbar(true); return;
          }
          
          const newTransaction = {
              // ... (newTransaction object construction using all state variables) ...
              attorney, assetCategory, payments, contactPerson, contactPerson2, contactPersonEmail,
              purchaser, purchased_date, sold_by, asset_description, occupied,
              monthly_rental_amount: monthlyRentalAmount, section_80_approval_date: section80ApprovalDate,
              auctioneer, auction_date: auctionDate, purchase_price: purchasePrice, valuationAmount, estForcedSaleValue,
              email_address: emailAddress, address, phone_no: phoneNo,
              email_address2: emailAddress2, email_address3: emailAddress3, mobile_number: mobileNumber,
              transaction_instruction_date: transactionInstructionDate, transfer_date: transferDate,
              costs_received_date: costsReceivedDate, section_89_costs: section89Costs,
              provisional_dividend_paid: provisionalDividendPaid, final_payment_date: finalPaymentDate,
              comments, registrationNumber, mortgageBondRegistered, bondBankName, bondAccountNo, bondNo, bondAddress,
              bondAdditionalAddress, bondFaxNumber, bondPhoneNo, bondEmailAddress,
              bondClaimProved, bondClaimAmount, bondReference, legalMatterId,
              hoaInformation, hoaStatement, municipalInformation, municipalStatement,
              caveatRegistered, linkedCreditor, linkedCashbookTransaction,
              partOfEstate,
              tenantName, tenantContact, tenantContactPerson,
              auctioneerContactPerson, auctioneerPhone, auctioneerEmail,
              erfNo, streetAddress, titleDeedNo, deedsOffice, caveatDate,
              caveatDocumentUrl, securityAtProperty, noticeToVacateDate,
              rentCurrentlyCollected, rentCollectedBy, rentalOtherInfo,
              tenderNo,
              vinNo,
              otherSubCategory,
              defName,
              defIdNo,
              bankName,
              accountNo,
              accountType,
              holdOnAccount,
              policyNo,
              inceptionDate,
              nameOfInsured,
              linkedRespondentId,
              linkedRespondentName,
              vehicleMake,
              vehicleModel,
              vehicleYear,
              insurerName,
              policyType,
              isMovableAssetEncumbered,
              movableEncumbranceType,
              otherMovableEncumbranceType,
              movableAuctioneerName,
              movableAuctioneerContactPerson,
              movableAuctioneerPhone,
              movableAuctioneerEmail,
              cashSource,
              otherCashSource,
              cashReceivedFrom,
              cashContactPerson,
              cashPhoneNo,
              cashEmailAddress,
              cashBankName,
              cashAccountHolder,
              cashAccountNumber,
              cashAccountType,
              supportingDocuments,
              depositPaid,
              depositAmount,
              proceedsReceivedDate,
              amountPaidToEstate,
              offerToPurchaseUrl,
              linkedClaimId,
              linkedClaimName,
              linkedClaimAmount,
              assetFinalised,
              status: ASSET_CATEGORIES[assetCategory], // Use the selected asset category as status

              // Duplicate fields for POST compatibility so they don't disappear on save
              purchasedDate: purchased_date,
              assetDescription: asset_description,
              monthlyRentalAmount: monthlyRentalAmount,
              section80ApprovalDate: section80ApprovalDate,
              auctionDate: auctionDate,
              purchasePrice: purchasePrice,
              emailAddress: emailAddress,
              phoneNo: phoneNo,
              mobileNumber: mobileNumber,
              transactionInstructionDate: transactionInstructionDate,
              transferDate: transferDate,
              costsReceivedDate: costsReceivedDate,
              section89Costs: section89Costs,
              provisionalDividendPaid: provisionalDividendPaid,
              finalPaymentDate: finalPaymentDate,
          };

          // Normalise every money field to a plain number before sending - the server's
          // Number()/sanitizeDecimal chokes on "1 964 495,30" / "1.964.495,30" and stores
          // null, which is why amounts weren't updating.
          const moneyKeys = [
              'payments', 'monthly_rental_amount', 'monthlyRentalAmount', 'purchase_price', 'purchasePrice',
              'valuationAmount', 'estForcedSaleValue', 'section_89_costs', 'section89Costs',
              'provisional_dividend_paid', 'provisionalDividendPaid', 'bondClaimAmount',
              'depositAmount', 'amountPaidToEstate', 'linkedClaimAmount',
          ];
          moneyKeys.forEach((k) => {
              if (newTransaction[k] === '' || newTransaction[k] === null || newTransaction[k] === undefined) {
                  newTransaction[k] = '';
              } else {
                  newTransaction[k] = parseMoney(newTransaction[k]);
              }
          });

          let response;
          let finalAsset;
          let currentAssets = [...assets];

          if (editingAsset && editingAsset.id) {
              // Update Logic
              response = await axios.put(
                  `/api/immoveables/${editingAsset.id}`,
                  newTransaction
              );
              finalAsset = sanitizeAsset(response.data);
              const index = currentAssets.findIndex(a => a.id === finalAsset.id);
              if (index !== -1) currentAssets[index] = finalAsset;
          } else {
              // Add Logic
              response = await axios.post(
                  "/api/immoveables",
                  newTransaction
              );
              finalAsset = sanitizeAsset(response.data);
              currentAssets.push(finalAsset);
          }
          
          setAssets(currentAssets);
          resetForm();
          setDialogOpen(false);
          
          setSnackbarMessage("Transaction saved successfully!");
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
          
      } catch (error) { // Catch and log the error
          console.error("Error saving transaction:", error);
          setSnackbarMessage("Error saving transaction.");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
      }
  };
    
    
    const handleEditTransaction = (asset) => {
        const selected = asset;
        if (!selected) return;
        setEditingAsset(asset);
        populateFormFields(asset);
        setDialogOpen(true);
    };
    

    const handleDeleteTransaction = async (assetId) => {
        if (!window.confirm("Are you sure you want to delete this asset?")) return;
        const transactionId = assetId;
        try {
            await axios.delete(`/api/immoveables/${transactionId}`);
            setAssets(assets.filter(a => a.id !== transactionId));
            setSnackbarMessage("Transaction deleted successfully!");
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
        } catch (error) {
            console.error("Error deleting transaction:", error);
            setSnackbarMessage("Error deleting transaction.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const handleCancelEdit = () => {
        resetForm();
        setEditingAsset(null);
        setDialogOpen(false);
    };

    // Fetch user on mount
    useEffect(() => {
        fetchUser();
        fetchAllClaims(); // Fetch all claims when component mounts
    }, []);

    // Reset to the first sub-tab whenever the main Asset Type changes (e.g. from Encumbered to Free Residue)
    // Removed: No longer needed as there are no sub-tabs

    // Fetch assets when assetType, selectedTab, or IDs change
    useEffect(() => {
        if (legalMatterId) {
            fetchAssets();
        }
    }, [legalMatterId, allClaims]); // Re-fetch assets if claims change
    
    return (
        <Box p={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">
                    All Assets
                </Typography>
                <Button
                    variant="contained"
                    onClick={handlePrintAssets}
                    disabled={!legalMatterId}
                >
                    Print Assets List
                </Button>
                <Button
                    variant="contained"
                    onClick={() => setAssetTypeSelectionOpen(true)} // Open asset type selection dialog
                    disabled={!legalMatterId}
                >
                    Add New Asset
                </Button>
            </Stack>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    {/* 2. Assets Table: "Asset Type" and "Category" used to be two separate
                                        columns both showing asset.assetCategory - consolidated into one. */}
                                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                                    {!isNPAMatter && (
                                        <>
                                            {/* 4.3/2: Valuation/Sold Amount/Linked Claim/Status are non-NPA only.
                                                Purchase Price, Purchaser and Claim Amount are still captured and
                                                editable on the asset itself - just not duplicated in this summary
                                                table, to keep it to the fields actually requested. */}
                                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Valuation Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Sold Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Linked Creditor</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Linked Claim No.</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Status</TableCell>
                                        </>
                                    )}
                                    {isNPAMatter && (
                                        <TableCell sx={{ fontWeight: 'bold' }}>Def/Resp</TableCell>
                                    )}
                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assets.map((asset) => (
                                    <TableRow key={asset.id} hover sx={{ backgroundColor: asset.assetFinalised ? '#e0ffe0' : 'inherit' }}>
                                        <TableCell>{getAssetDescription(asset, isNPAMatter)}</TableCell>
                                        <TableCell>{asset.assetCategory || 'N/A'}</TableCell>
                                        {!isNPAMatter && (
                                            <>
                                                <TableCell align="right">{asset.valuationAmount ? formatMoney(asset.valuationAmount) : 'N/A'}</TableCell>
                                                <TableCell align="right">{asset.amountPaidToEstate ? formatMoney(asset.amountPaidToEstate) : 'N/A'}</TableCell>
                                                <TableCell>{asset.linkedClaimName || 'N/A'}</TableCell>
                                                <TableCell>{asset.linkedClaimId ? (allClaims.find(c => String(c.id) === String(asset.linkedClaimId))?.claimNumber || asset.linkedClaimId) : 'N/A'}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title={asset.assetFinalised ? 'Asset dealt with / finalised' : 'Asset still requires attention'}>
                                                        <Chip
                                                            size="small"
                                                            label={asset.assetFinalised ? 'Dealt With' : 'Outstanding'}
                                                            sx={{
                                                                bgcolor: asset.assetFinalised ? '#2e7d32' : '#c62828',
                                                                color: '#fff',
                                                                fontWeight: 'bold',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </TableCell>
                                            </>
                                        )}
                                        {isNPAMatter && (
                                            <TableCell>{asset.linkedRespondentName || asset.defName || 'N/A'}</TableCell>
                                        )}
                                        <TableCell align="center">
                                            {isNPAMatter && asset.assetFinalised && <Chip label="Finalised" color="success" size="small" sx={{ mr: 1 }} />}
                                            <IconButton size="small" color="primary" onClick={() => handleEditTransaction(asset)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteTransaction(asset.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {assets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={isNPAMatter ? 4 : 8} align="center" sx={{ fontStyle: 'italic', color: 'text.secondary', py: 4 }}>No assets found. Click "Add New Asset" to begin.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Dialog open={dialogOpen} onClose={handleCancelEdit} fullWidth maxWidth="md">
                        <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
                        <DialogContent>
                            {(assetCategory === 'Immovable Property') && (
                            <>
                        {/* 1.1 Property Information */}
                        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>1.1 Property Information</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="a) Property Description"
                                        value={asset_description}
                                        onChange={(e) => setAssetDescription(e.target.value)}
                                        disabled={user?.role === 'guest'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="b) Erf / Stand / Portion No" value={erfNo} onChange={(e) => setErfNo(e.target.value)} disabled={user?.role === 'guest'} placeholder="e.g. Erf 1234, Bloemfontein" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="c) Known Physical Address of the Property" value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} disabled={user?.role === 'guest'} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Property Valuation Amount"
                                        type="text"
                                        value={valuationAmount}
                                        onChange={(e) => setValuationAmount(e.target.value)}
                                        disabled={user?.role === 'guest'}
                                        InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Est. Forced Sale Value"
                                        type="text"
                                        value={estForcedSaleValue}
                                        onChange={(e) => setEstForcedSaleValue(e.target.value)}
                                        disabled={user?.role === 'guest'}
                                        InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                        helperText="Feeds the Curator's Report Section 9 schedule"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="d) Deeds Office" value={deedsOffice} onChange={(e) => setDeedsOffice(e.target.value)} disabled={user?.role === 'guest'} placeholder="e.g. Registrar Bloemfontein" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="e) Title Deed No" value={titleDeedNo} onChange={(e) => setTitleDeedNo(e.target.value)} disabled={user?.role === 'guest'} placeholder="e.g. T0000/2222" />
                                </Grid>
                                <Grid item xs={12} sm={caveatRegistered ? 6 : 12}>
                                    <FormControlLabel
                                        control={<Checkbox checked={caveatRegistered} onChange={(e) => setCaveatRegistered(e.target.checked)} name="caveatRegistered" disabled={user?.role === 'guest'} />}
                                        label="f) Caveat Registered against Property"
                                    />
                                </Grid>
                                {caveatRegistered && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Caveat Date"
                                            type="date"
                                            value={caveatDate}
                                            onChange={(e) => setCaveatDate(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ max: '9999-12-31' }}
                                        />
                                    </Grid>
                                )}
                                {/* 4.4: link/upload the caveat document - non-NPA only */}
                                {caveatRegistered && !isNPAMatter && (
                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={caveatDocumentUrl ? <VisibilityIcon /> : <AttachmentIcon />}
                                            color={caveatDocumentUrl ? "success" : "primary"}
                                            disabled={user?.role === 'guest'}
                                            onClick={() => { setDocPickerTarget('caveat'); setDocPickerOpen(true); }}
                                        >
                                            {caveatDocumentUrl ? 'View Caveat Document' : 'Attach Caveat Document'}
                                        </Button>
                                    </Grid>
                                )}
                                {/* 4.4: Security at Property - non-NPA only */}
                                {!isNPAMatter && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<Checkbox checked={securityAtProperty} onChange={(e) => setSecurityAtProperty(e.target.checked)} name="securityAtProperty" disabled={user?.role === 'guest'} />}
                                            label="g) Security is currently placed at the property"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>

                        {/* ❌ REMOVED FOR 'free residue': Bond Holder / Creditors Information */}
                        {/* 4.2: Mortgage Bond section is non-NPA only */}
                        {(assetCategory === 'Immovable Property' && !isNPAMatter) && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>1.2. Mortgage Bond Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={mortgageBondRegistered}
                                                    onChange={(e) => setMortgageBondRegistered(e.target.checked)}
                                                    disabled={user?.role === 'guest'}
                                                />
                                            }
                                            label="a) Is the property encumbered by a mortgage bond?"
                                        />
                                    </Grid>
                                    {mortgageBondRegistered && (
                                        <>
                                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">b) If Yes, capture:</Typography></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Bond Holder" value={bondBankName} onChange={(e) => setBondBankName(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Mortgage Bond Account Number" value={bondAccountNo} onChange={(e) => setBondAccountNo(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Outstanding Mortgage Bond Amount"
                                                    type="text"
                                                    value={bondClaimAmount}
                                                    onChange={(e) => setBondClaimAmount(e.target.value)}
                                                    disabled={user?.role === 'guest'}
                                                    InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Bond Holder Contact Person" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Telephone Number" value={bondPhoneNo} onChange={(e) => setBondPhoneNo(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Email Address" value={bondEmailAddress} onChange={(e) => setBondEmailAddress(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12}><TextField fullWidth label="Physical Address" value={bondAddress} onChange={(e) => setBondAddress(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                        </>
                                    )}
                                </Grid>
                            </Paper>
                        )}

                        {/* 1.3. Occupancy Information */}
                        {(assetCategory === 'Immovable Property') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>1.3. Occupancy Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="a) Occupancy Status"
                                            value={occupied === 'No' ? 'Vacant' : 'Occupied'}
                                            onChange={(e) => setOccupied(e.target.value === 'Vacant' ? 'No' : (occupied === 'Insolvent' ? 'Insolvent' : 'Tenant'))}
                                            disabled={user?.role === 'guest'}
                                        >
                                            <MenuItem value="Occupied">Occupied</MenuItem>
                                            <MenuItem value="Vacant">Vacant</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={occupied === 'Insolvent'} onChange={(e) => setOccupied(e.target.checked ? 'Insolvent' : 'Tenant')} disabled={user?.role === 'guest' || occupied === 'No'} />}
                                            label={isNPAMatter ? 'b) Tenant currently resides in the property' : 'b) Insolvent currently resides in the property'}
                                        />
                                    </Grid>

                                    {occupied !== 'No' && occupied !== 'Insolvent' && (
                                        <>
                                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">c) If the property is tenanted, capture:</Typography></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Tenant's Name & Surname" value={tenantName} onChange={(e) => setTenantName(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Contact Person (if applicable)" value={tenantContactPerson} onChange={(e) => setTenantContactPerson(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Contact Details (Telephone Number)" value={tenantContact} onChange={(e) => setTenantContact(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                            <Grid item xs={12} sm={6}><TextField fullWidth label="Email Address" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                        </>
                                    )}

                                    {/* 4.4: Notice to Vacate - diarised/reminded, non-NPA only */}
                                    {occupied !== 'No' && !isNPAMatter && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Notice to Vacate Date"
                                                type="date"
                                                value={noticeToVacateDate}
                                                onChange={(e) => setNoticeToVacateDate(e.target.value)}
                                                disabled={user?.role === 'guest'}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ max: '9999-12-31' }}
                                                helperText="You'll be reminded when this date is due"
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="d) Monthly Rental Amount" type="text" value={monthlyRentalAmount} onChange={(e) => setMonthlyRentalAmount(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>

                                    {/* 4.4: Rental Information - non-NPA only */}
                                    {!isNPAMatter && (
                                        <>
                                            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FormControlLabel
                                                    control={<Checkbox checked={rentCurrentlyCollected} onChange={(e) => setRentCurrentlyCollected(e.target.checked)} name="rentCurrentlyCollected" disabled={user?.role === 'guest'} />}
                                                    label="e) Rent is currently being collected"
                                                />
                                            </Grid>
                                            {rentCurrentlyCollected && (
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth label="Rent Collected By" value={rentCollectedBy} onChange={(e) => setRentCollectedBy(e.target.value)} disabled={user?.role === 'guest'} placeholder="e.g. Estate agent, Trustee, etc." />
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <TextField fullWidth multiline rows={2} label="f) Other Relevant Rental Information" value={rentalOtherInfo} onChange={(e) => setRentalOtherInfo(e.target.value)} disabled={user?.role === 'guest'} />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Paper>
                        )}
        
                        {/* Sale Of Asset */}
                        {(assetCategory === 'Immovable Property') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>1.4. Auctioneer Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="a) Type of Sale"
                                            value={sold_by}
                                            onChange={(e) => setSoldBy(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                        >
                                            <MenuItem value="Private Sale">Private Sale</MenuItem>
                                            <MenuItem value="Auction">Auction</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="b) Date Offer to Purchase Signed" type="date" InputLabelProps={{ shrink: true }} value={purchased_date} onChange={(e) => setPurchasedDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} /></Grid>
                                    {sold_by === 'Auction' && (
                                        <Grid item xs={12} sm={6}><TextField fullWidth label="c) Date of Auction (if applicable)" type="date" InputLabelProps={{ shrink: true }} value={auctionDate} onChange={(e) => setAuctionDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} /></Grid>
                                    )}
                                    {/* Auctioneer / selling agent firm details - always available regardless of sale type */}
                                    <Grid item xs={12}><Typography variant="body2" color="text.secondary">Auctioneer firm details:</Typography></Grid>
                                    <Grid item xs={12} sm={4}><TextField fullWidth label="Name of Auctioneer Firm" value={auctioneer} onChange={(e) => setAuctioneer(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                    <Grid item xs={12} sm={4}><TextField fullWidth label="Person Dealing With" value={auctioneerContactPerson} onChange={(e) => setAuctioneerContactPerson(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                    <Grid item xs={12} sm={4}><TextField fullWidth label="Contact Number" value={auctioneerPhone} onChange={(e) => setAuctioneerPhone(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                    <Grid item xs={12} sm={4}><TextField fullWidth label="Email Address" value={auctioneerEmail} onChange={(e) => setAuctioneerEmail(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="d) Purchase Price" type="text" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel control={<Checkbox checked={depositPaid} onChange={(e) => setDepositPaid(e.target.checked)} name="depositPaid" />} label="e) Deposit Paid?" />
                                    </Grid>
                                    {depositPaid && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="f) Deposit Amount" type="text" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="g) Property Transfer Date" type="date" InputLabelProps={{ shrink: true }} value={transferDate} onChange={(e) => setTransferDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="h) Proceeds Received Date" type="date" InputLabelProps={{ shrink: true }} value={proceedsReceivedDate} onChange={(e) => setProceedsReceivedDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="i) Amount Paid into the Estate" type="text" value={amountPaidToEstate} onChange={(e) => setAmountPaidToEstate(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
        
                        {/* Transfer Information (only for Immovable Property) */}
                        {(assetCategory === 'Immovable Property') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>1.5. Transfer Attorneys</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}><Typography variant="body2" color="text.secondary">a) Capture:</Typography></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Firm Name" value={attorney} onChange={(e) => setAttorney(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Contact Person" value={contactPerson2} onChange={(e) => setContactPerson2(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Telephone Number" value={phoneNo} onChange={(e) => setPhoneNo(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Email Address" value={emailAddress2} onChange={(e) => setEmailAddress2(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                            </>
                            )}

                        {(assetCategory === 'Movable Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>2.1. Asset Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="a) Asset Category"
                                            value={assetCategory}
                                            onChange={(e) => setAssetCategory(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                        >
                                            <MenuItem value="Movable Asset">Movable Asset</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="b) Asset Description"
                                            value={asset_description}
                                            onChange={(e) => setAssetDescription(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="c) Known Address/Location of the Asset" value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="d) Valuation Amount"
                                            type="text"
                                            value={valuationAmount}
                                            onChange={(e) => setValuationAmount(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                            InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Est. Forced Sale Value"
                                            type="text"
                                            value={estForcedSaleValue}
                                            onChange={(e) => setEstForcedSaleValue(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                            InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                            helperText="Feeds the Curator's Report Section 9 schedule"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Movable Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>2.2. Encumbrance Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControlLabel control={<Checkbox checked={isMovableAssetEncumbered} onChange={(e) => setIsMovableAssetEncumbered(e.target.checked)} name="isMovableAssetEncumbered" />} label="a) Is the asset encumbered?" />
                                    </Grid>
                                    {isMovableAssetEncumbered && (
                                        <>
                                            <Grid item xs={12} sm={6}>
                                                <TextField select fullWidth label="b) Type of Encumbrance" value={movableEncumbranceType} onChange={(e) => setMovableEncumbranceType(e.target.value)}>
                                                    <MenuItem value="Instalment Sale Agreement">Instalment Sale Agreement</MenuItem>
                                                    <MenuItem value="Retention of Title">Retention of Title</MenuItem>
                                                    <MenuItem value="Other">Other</MenuItem>
                                                </TextField>
                                            </Grid>
                                            {movableEncumbranceType === 'Other' && (
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth label="c) Specify Other Encumbrance" value={otherMovableEncumbranceType} onChange={(e) => setOtherMovableEncumbranceType(e.target.value)} />
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 1 }}><Typography variant="caption">d) Secured Creditor's Details</Typography></Divider>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Creditor Name" value={bondBankName} onChange={(e) => setBondBankName(e.target.value)} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Contact Person" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Telephone Number" value={bondPhoneNo} onChange={(e) => setBondPhoneNo(e.target.value)} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Email Address" value={bondEmailAddress} onChange={(e) => setBondEmailAddress(e.target.value)} />
                                            </Grid>
                                            <Grid item xs={12}><TextField fullWidth label="Physical Address (optional)" value={bondAddress} onChange={(e) => setBondAddress(e.target.value)} /></Grid>
                                        </>
                                    )}
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Movable Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>2.3. Auctioneer Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}><Typography variant="body2" color="text.secondary">a) Capture the appointed auctioneer's details:</Typography></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Company Name" value={movableAuctioneerName} onChange={(e) => setMovableAuctioneerName(e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Contact Person" value={movableAuctioneerContactPerson} onChange={(e) => setMovableAuctioneerContactPerson(e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="Telephone Number" value={movableAuctioneerPhone} onChange={(e) => setMovableAuctioneerPhone(e.target.value)} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="Email Address" value={movableAuctioneerEmail} onChange={(e) => setMovableAuctioneerEmail(e.target.value)} /></Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Movable Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>2.4. Sale of Asset</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="a) Type of Sale"
                                            value={sold_by}
                                            onChange={(e) => setSoldBy(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                        >
                                            <MenuItem value="Private Sale">Private Sale</MenuItem>
                                            <MenuItem value="Auction">Auction</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="b) Date Offer to Purchase Signed" type="date" InputLabelProps={{ shrink: true }} value={purchased_date} onChange={(e) => setPurchasedDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} />
                                    </Grid>
                                    {sold_by === 'Auction' && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="c) Date of Auction (if applicable)" type="date" InputLabelProps={{ shrink: true }} value={auctionDate} onChange={(e) => setAuctionDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="d) Purchase Price" type="text" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="e) Proceeds Received Date" type="date" InputLabelProps={{ shrink: true }} value={proceedsReceivedDate} onChange={(e) => setProceedsReceivedDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="f) Amount Paid into the Estate" type="text" value={amountPaidToEstate} onChange={(e) => setAmountPaidToEstate(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Movable Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>2.5. Estate Integration</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}><Typography variant="body2" color="text.secondary">a) Please include the following functionality:</Typography></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Link to Creditor Claim" value={linkedCreditor} onChange={(e) => setLinkedCreditor(e.target.value)} disabled={user?.role === 'guest'} placeholder="Enter Creditor Name or ID" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Link to Cashbook (Sale Proceeds)" value={linkedCashbookTransaction} onChange={(e) => setLinkedCashbookTransaction(e.target.value)} disabled={user?.role === 'guest'} placeholder="Enter Cashbook Transaction Ref" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Button fullWidth variant="outlined" startIcon={offerToPurchaseUrl ? <VisibilityIcon /> : <AttachmentIcon />} onClick={() => setDocPickerOpen(true)} color={offerToPurchaseUrl ? "success" : "primary"}>
                                            {offerToPurchaseUrl ? 'View Offer to Purchase' : 'Attach Offer to Purchase'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Movable Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>2.7. Asset Status</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<Checkbox checked={assetFinalised} onChange={(e) => setAssetFinalised(e.target.checked)} name="assetFinalised" disabled={user?.role === 'guest'} />}
                                            label="Asset has been dealt with and finalised"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Vehicle') && (
                            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Vehicle Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Make" value={vehicleMake} onChange={(e) => setVehicleMake(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Model" value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="Year" value={vehicleYear} onChange={(e) => setVehicleYear(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="Registration Number" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="VIN / Engine Number" value={vinNo} onChange={(e) => setVinNo(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Current Value" type="text" value={valuationAmount} onChange={(e) => setValuationAmount(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Est. Forced Sale Value" type="text" value={estForcedSaleValue} onChange={(e) => setEstForcedSaleValue(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} helperText="Feeds the Curator's Report Section 9 schedule" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth multiline rows={2} label="Additional Description" value={asset_description} onChange={(e) => setAssetDescription(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Policy') && (
                            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Policy Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Insurer Name" value={insurerName} onChange={(e) => setInsurerName(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Policy Number" value={policyNo} onChange={(e) => setPolicyNo(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Policy Type" value={policyType} onChange={(e) => setPolicyType(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Inception Date" type="date" InputLabelProps={{ shrink: true }} value={inceptionDate} onChange={(e) => setInceptionDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Name of Insured" value={nameOfInsured} onChange={(e) => setNameOfInsured(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Insured / Sum Assured Amount" type="text" value={valuationAmount} onChange={(e) => setValuationAmount(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth multiline rows={2} label="Additional Description" value={asset_description} onChange={(e) => setAssetDescription(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Bank Account') && (
                            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>3.1. Source of Funds</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="a) How was the cash obtained?"
                                            value={cashSource}
                                            onChange={(e) => setCashSource(e.target.value)}
                                            disabled={user?.role === 'guest'}
                                        >
                                            <MenuItem value="Cash on Hand">Cash on Hand</MenuItem>
                                            <MenuItem value="Monies in Bank Account">Monies in Bank Account</MenuItem>
                                            <MenuItem value="Contribution to Estate">Contribution to Estate</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </TextField>
                                    </Grid>
                                    {cashSource === 'Other' && (
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Please specify" value={otherCashSource} onChange={(e) => setOtherCashSource(e.target.value)} disabled={user?.role === 'guest'} />
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Bank Account') && (
                            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>3.2. Source Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}><Typography variant="body2" color="text.secondary">a) Capture the details of where the funds originated:</Typography></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="Received From (Individual, Company or Institution)" value={cashReceivedFrom} onChange={(e) => setCashReceivedFrom(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="Contact Person (where applicable)" value={cashContactPerson} onChange={(e) => setCashContactPerson(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="Telephone Number" value={cashPhoneNo} onChange={(e) => setCashPhoneNo(e.target.value)} disabled={user?.role === 'guest'} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="Email Address" value={cashEmailAddress} onChange={(e) => setCashEmailAddress(e.target.value)} disabled={user?.role === 'guest'} /></Grid>

                                    {/* Def/Resp link now lives in the shared "Linked Defendant / Respondent" section below */}

                                    {cashSource === 'Monies in Bank Account' && (
                                        <>
                                            <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption">b) Bank Account Information</Typography></Divider></Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Bank Name" value={cashBankName} onChange={(e) => setCashBankName(e.target.value)} disabled={user?.role === 'guest'} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Account Holder" value={cashAccountHolder} onChange={(e) => setCashAccountHolder(e.target.value)} disabled={user?.role === 'guest'} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Account Number" value={cashAccountNumber} onChange={(e) => setCashAccountNumber(e.target.value)} disabled={user?.role === 'guest'} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Account Type" value={cashAccountType} onChange={(e) => setCashAccountType(e.target.value)} disabled={user?.role === 'guest'} />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Bank Account') && (
                            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>3.3. Estate Receipt Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="a) Amount Paid into the Estate" type="text" value={amountPaidToEstate} onChange={(e) => setAmountPaidToEstate(e.target.value)} disabled={user?.role === 'guest'} InputProps={{ startAdornment: <CurrencyAdornment /> }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="b) Date Funds Received" type="date" InputLabelProps={{ shrink: true }} value={proceedsReceivedDate} onChange={(e) => setProceedsReceivedDate(e.target.value)} disabled={user?.role === 'guest'} inputProps={{ max: '9999-12-31' }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="c) Reference/Description (optional)" value={asset_description} onChange={(e) => setAssetDescription(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Bank Account') && (
                            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>3.4. Estate Integration</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}><Typography variant="body2" color="text.secondary">a) Please include the following functionality:</Typography></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Link to Estate Cashbook" value={linkedCashbookTransaction} onChange={(e) => setLinkedCashbookTransaction(e.target.value)} disabled={user?.role === 'guest'} placeholder="Enter Cashbook Transaction Ref" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Button fullWidth variant="outlined" startIcon={offerToPurchaseUrl ? <VisibilityIcon /> : <AttachmentIcon />} onClick={() => setDocPickerOpen(true)} color={offerToPurchaseUrl ? "success" : "primary"}>
                                            {offerToPurchaseUrl ? 'View Proof of Funds' : 'Attach Proof of Funds'}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }}><Typography variant="caption">Supporting Documents</Typography></Divider>
                                        {supportingDocuments.map((doc, index) => (
                                            <Chip
                                                key={index}
                                                label={doc.split('/').pop()}
                                                onDelete={() => setSupportingDocuments(docs => docs.filter((_, i) => i !== index))}
                                                sx={{ mr: 1, mb: 1 }}
                                            />
                                        ))}
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<AttachmentIcon />}
                                            onClick={() => setDocPickerOpen(true)}
                                        >
                                            Add Supporting Document
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Bank Account') && (
                            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>3.5. Asset Status</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<Checkbox checked={assetFinalised} onChange={(e) => setAssetFinalised(e.target.checked)} name="assetFinalised" disabled={user?.role === 'guest'} />}
                                            label="Asset has been dealt with and finalised"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {(assetCategory === 'Other Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>Other assets in favour of the Estate</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="Def/Resp Name" value={purchaser} onChange={(e) => setPurchaser(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="Registration No of Company" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth label="Shareholding %" type="number" value={monthlyRentalAmount} onChange={(e) => setMonthlyRentalAmount(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth multiline rows={3} label="Asset Description" value={asset_description} onChange={(e) => setAssetDescription(e.target.value)} disabled={user?.role === 'guest'} />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {/* Estate Integration (for Immovable and Movable) */}
                        {(assetCategory === 'Immovable Property' || assetCategory === 'Movable Asset' || assetCategory === 'Vehicle' || assetCategory === 'Policy') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>1.6. Estate Integration</Typography>
                                <Grid container spacing={2}>
                                    {!isNPAMatter && (
                                        <>
                                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">a) Please include the following functionality:</Typography></Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField fullWidth label="Link to Creditor Claim" value={linkedCreditor} onChange={(e) => setLinkedCreditor(e.target.value)} disabled={user?.role === 'guest'} placeholder="Enter Creditor Name or ID" />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Link to Cashbook (Sale Proceeds)" value={linkedCashbookTransaction} onChange={(e) => setLinkedCashbookTransaction(e.target.value)} disabled={user?.role === 'guest'} placeholder="Enter Cashbook Transaction Ref" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Button fullWidth variant="outlined" startIcon={offerToPurchaseUrl ? <VisibilityIcon /> : <AttachmentIcon />} onClick={() => setDocPickerOpen(true)} color={offerToPurchaseUrl ? "success" : "primary"}>
                                            {offerToPurchaseUrl ? 'View Offer to Purchase' : 'Attach Offer to Purchase'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
        
                        {/* Asset Status (for Immovable and Movable) */}
                        {(assetCategory === 'Immovable Property' || assetCategory === 'Movable Asset' || assetCategory === 'Vehicle' || assetCategory === 'Policy' || assetCategory === 'Other Asset') && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>{isNPAMatter ? 'Name of Purchaser' : '1.7. Asset Status'}</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={<Checkbox checked={assetFinalised} onChange={(e) => setAssetFinalised(e.target.checked)} name="assetFinalised" disabled={user?.role === 'guest'} />}
                                            label="Asset has been dealt with and finalised"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                        {/* 🎯 NEW: Link to Claim */}
                        {!isNPAMatter && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Link to Claim</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            fullWidth
                                            options={allClaims}
                                            getOptionLabel={(option) => `${option.creditorName} - ${option.claimNumber} (R ${parseFloat(option.provisionalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })})`}
                                            value={allClaims.find(claim => String(claim.id) === String(linkedClaimId)) || null}
                                            onChange={(event, newValue) => {
                                                setLinkedClaimId(newValue ? newValue.id : '');
                                                setLinkedClaimName(newValue ? newValue.creditorName : '');
                                                setLinkedClaimAmount(newValue ? newValue.provisionalValue : '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Link to Claim" variant="outlined" />
                                            )}
                                            disabled={user?.role === 'guest'}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {/* Link the asset to a Defendant / Respondent - available for every
                            asset category on NPA / Curator matters (drives the Curator's
                            Report per-Defendant Assets & Liabilities grouping). */}
                        {isNPAMatter && assetCategory && (
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Linked Defendant / Respondent</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            fullWidth
                                            options={matter?.respondents || []}
                                            getOptionLabel={(option) => `${option.name}${option.type ? ` (${option.type})` : ''}`}
                                            value={(matter?.respondents || []).find((r) => String(r.id) === String(linkedRespondentId)) || null}
                                            onChange={(event, newValue) => {
                                                setLinkedRespondentId(newValue ? newValue.id : '');
                                                setLinkedRespondentName(newValue ? newValue.name : '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Asset belongs to (Defendant / Respondent)" variant="outlined" />
                                            )}
                                            disabled={user?.role === 'guest'}
                                        />
                                        {(!matter?.respondents || matter.respondents.length === 0) && (
                                            <Typography variant="caption" color="text.secondary">
                                                Add Defendants / Respondents on the Estate Information tab first.
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {/* Comments (always visible) */}
                        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1">Comments</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                disabled={user?.role === 'guest'}
                            />
                        </Paper>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                            <Button 
                                variant="contained" 
                                onClick={handleAddOrSaveTransaction}
                                disabled={user?.role === 'guest'}
                            >
                                {editingAsset ? 'Update Asset' : 'Save Asset'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {/* Asset Type Selection Dialog */}
                    <Dialog open={assetTypeSelectionOpen} onClose={() => setAssetTypeSelectionOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Select Asset Type</DialogTitle>
                        <DialogContent>
                            <TextField
                                select
                                fullWidth
                                label="Asset Type"
                                value={selectedAssetTypeForNew}
                                onChange={(e) => setSelectedAssetTypeForNew(e.target.value)}
                                sx={{ mt: 1 }}
                            >
                                {Object.keys(ASSET_CATEGORIES)
                                    .filter((category) => !NPA_ONLY_CATEGORIES.includes(category) || isNPAMatter)
                                    .map((category) => (
                                        <MenuItem key={category} value={category}>{category}</MenuItem>
                                    ))}
                            </TextField>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setAssetTypeSelectionOpen(false)}>Cancel</Button>
                            <Button onClick={() => { setAssetCategory(selectedAssetTypeForNew); setAssetTypeSelectionOpen(false); setDialogOpen(true); }} variant="contained" disabled={!selectedAssetTypeForNew}>
                                Select
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {/* Document Picker Modal */}
                    <DocumentPicker
                        open={docPickerOpen}
                        onClose={() => setDocPickerOpen(false)}
                        matterId={legalMatterId}
                        user={user}
                        onDocumentSelect={(filepath) => {
                            if (docPickerTarget === 'caveat') {
                                setCaveatDocumentUrl(filepath);
                            } else if (assetCategory === 'Bank Account') {
                                if (!offerToPurchaseUrl) {
                                    setOfferToPurchaseUrl(filepath);
                                } else {
                                    setSupportingDocuments(docs => [...docs, filepath]);
                                }
                            } else {
                                setOfferToPurchaseUrl(filepath);
                            }
                            setDocPickerOpen(false); // Close picker after selection
                        }}
                        folder={docPickerTarget === 'caveat' ? "Caveat Documents" : (assetCategory === 'Bank Account' ? "Proof of Funds" : "Offers to Purchase")}
                        activeDocPath={docPickerTarget === 'caveat' ? caveatDocumentUrl : offerToPurchaseUrl}
                    />
            {/* Snackbar */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ImmoveableAssetsTab;